import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import type { RuntimeConfigAuthOidc, RuntimeConfigDocument } from '@albuelis-accounting/shared';

/**
 * The OIDC client.
 *
 * Authorization Code flow with PKCE against the Cognito user pool that
 * `albuelis-idp` operates. The browser is a **public client**: there is no secret
 * here, none in the bundle, and none in the runtime configuration document.
 *
 * Every value comes from that document, so this module hardcodes nothing about any
 * environment, with one deliberate exception: the redirect URIs are derived from the
 * origin the page is actually served from, so the same deployed configuration works from
 * `http://localhost:5173` (pointed at a deployed dev/test API) and from the real domain.
 * Both origins must be registered for this app in `albuelis-idp`; Cognito rejects any
 * other redirect.
 */

let manager: UserManager | undefined;

/** The path the identity provider redirects back to after sign-in. */
export const CALLBACK_PATH = '/auth/callback';

/**
 * The auth settings with the redirect URIs bound to the current origin. Without this,
 * running the SPA on localhost against a deployed environment would send the browser
 * back to the deployed site after sign-in instead of to the local dev server.
 */
function oidcAuth(config: RuntimeConfigDocument): RuntimeConfigAuthOidc {
  const origin = window.location.origin;
  return {
    ...config.auth,
    redirectUri: `${origin}${CALLBACK_PATH}`,
    postLogoutRedirectUri: origin,
  };
}

/**
 * Builds (or returns) the user manager.
 *
 * Constructed lazily because it needs the configuration document, which is not
 * available at module load.
 */
export function userManager(config: RuntimeConfigDocument): UserManager {
  if (manager) return manager;
  const auth = oidcAuth(config);

  manager = new UserManager({
    authority: auth.authority,
    client_id: auth.clientId,
    redirect_uri: auth.redirectUri,
    post_logout_redirect_uri: auth.postLogoutRedirectUri,
    response_type: 'code',
    scope: auth.scopes.join(' '),

    // PKCE is on by default for the code flow in oidc-client-ts; stated explicitly
    // because it is a security property of this client rather than a default worth
    // inheriting silently.
    disablePKCE: false,

    // Tokens live in session storage, not local storage: closing the tab ends the
    // session, a token is not left behind on a shared machine, and the blast radius
    // of a cross-site scripting bug stays within the current tab.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

    // Refresh silently before expiry so a long session is not interrupted by a
    // redirect that would lose unsaved form state. Cognito issues a refresh token to
    // a public client with the code flow, which makes this possible without an iframe.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 120,

    // The authorization code is exchanged and then removed from the URL by the
    // callback route, so it never lingers in history or in a shared link.
    loadUserInfo: false,

    // Cognito's OIDC discovery document does not advertise `end_session_endpoint`,
    // so `signoutRedirect` cannot be used. Sign-out is a deliberate redirect to
    // Cognito's `/logout`, built in `signOut` below.
    metadataSeed: {},
  });

  return manager;
}

/** Test seam: drop the memoised manager. */
export function resetUserManager(): void {
  manager = undefined;
}

/** Starts the sign-in redirect, remembering where the user was heading. */
export async function signIn(config: RuntimeConfigDocument, returnTo: string): Promise<void> {
  await userManager(config).signinRedirect({
    // Round-tripped through the authorization request's state, so a user who deep
    // linked to an invoice lands on that invoice rather than on the dashboard.
    state: { returnTo },
  });
}

/**
 * Signs out everywhere.
 *
 * Clears the local session first, then redirects to Cognito's `/logout`, which ends
 * the session on the identity provider too. Without the second step the user would
 * appear signed out and then be signed straight back in by the still-live Cognito
 * session on the next sign-in attempt — which looks like the sign-out button not
 * working.
 *
 * Built by hand because Cognito's `/logout` takes `client_id` and `logout_uri`
 * rather than the standard `post_logout_redirect_uri`, and is not advertised in the
 * discovery document at all.
 */
export async function signOut(config: RuntimeConfigDocument): Promise<void> {
  const auth = oidcAuth(config);
  await userManager(config).removeUser();

  const logoutUrl = new URL(`https://${auth.hostedLoginDomain}/logout`);
  logoutUrl.searchParams.set('client_id', auth.clientId);
  logoutUrl.searchParams.set('logout_uri', auth.postLogoutRedirectUri);
  window.location.assign(logoutUrl.toString());
}

/** Completes the redirect, returning the user and where they were heading. */
export async function completeSignIn(
  config: RuntimeConfigDocument,
): Promise<{ user: User; returnTo: string }> {
  const user = await userManager(config).signinCallback();
  if (!user) {
    throw new Error('The sign-in response did not contain a session.');
  }
  const state = user.state as { returnTo?: unknown } | undefined;
  const returnTo =
    typeof state?.returnTo === 'string' && state.returnTo.startsWith('/') ? state.returnTo : '/';
  return { user, returnTo };
}

/** The current session, or `undefined` when there is none or it has expired. */
export async function currentUser(config: RuntimeConfigDocument): Promise<User | undefined> {
  const user = await userManager(config).getUser();
  if (!user || user.expired) return undefined;
  return user;
}

/**
 * The token sent to the API.
 *
 * The **ID token**, deliberately. The API's authoriser verifies it with
 * `tokenUse: 'id'` and checks `aud` against this application's client id, which is
 * what distinguishes a user of this application from a user of anything else in the
 * shared pool. It also carries the `email` and `name` claims the backend stamps as
 * display-only snapshots — a Cognito access token carries neither.
 */
export function bearerToken(user: User): string {
  return user.id_token ?? '';
}
